import { useCallback } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router-dom';
import { I18N_ROUTING_ENABLED, type AppLocale } from './locales';
import { translatePath } from './slugMap';
import { useCurrentLocale } from './useLocalizedPath';

/**
 * Wrapper de `useNavigate()` que respeita o idioma corrente — espelha as
 * regras do `LocalizedLink` para `navigate(...)` programáticos:
 *
 * - Flag `I18N_ROUTING_ENABLED` off ou locale = pt-BR → comportamento idêntico
 *   ao `useNavigate()` cru.
 * - Path já contém prefix `/en/` ou `/es/` → passa direto.
 * - Path conhecido (rota top-level ou multi-segmento) → traduz via
 *   `translatePath` preservando segmentos dinâmicos (IDs numéricos, etc.).
 * - Path não-mapeado (rota dinâmica desconhecida) → fallback de prepend
 *   simples `/${locale}${path}`.
 *
 * Retorna uma função compatível com `useNavigate()` (suporta `(delta)` ou
 * `(path, opts?)`). Não aceita LocationObject — todos os usos atuais no app
 * passam string ou número.
 */
export function useLocalizedNavigate(): (
  to: string | number,
  options?: NavigateOptions,
) => void {
  const navigate = useNavigate();
  const locale = useCurrentLocale();

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }
      if (!I18N_ROUTING_ENABLED || locale === 'pt-BR') {
        navigate(to, options);
        return;
      }
      if (/^\/(en|es)(\/|$|#|\?)/.test(to)) {
        navigate(to, options);
        return;
      }
      if (to.startsWith('#') || !to.startsWith('/')) {
        // Relative ou anchor — não tem como aplicar prefix sem location atual.
        navigate(to, options);
        return;
      }
      const translated = translatePath(to, locale as AppLocale);
      navigate(translated ?? `/${locale}${to}`, options);
    },
    [navigate, locale],
  );
}

export default useLocalizedNavigate;
